import { format, parseISO } from 'date-fns';
import {
  Copy,
  DatabaseIcon,
  DotsThreeVertical,
  FolderMinus,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { TableCell } from '@/components/ui-kit/table';
import type { ContactsTableColumnId } from '../../config/contacts-table-columns';
import { parseCustomAttributeColumnKey } from '../../config/contacts-table-columns';
import { ContactSubscriptionStatusPill } from '../contact-subscription-status-pill/contact-subscription-status-pill';
import type { CustomFieldDefinition } from '../../types/custom-field-definitions.types';
import type { Contact } from '../../types/contacts.types';
import { getContactCustomFieldDisplayValue } from '../../utils/custom-field-attributes.utils';
import {
  getContactDisplayName,
  getContactInitials,
  getContactLists,
} from '../../utils/contact-attributes';
import { getContactColumnDisplayValue } from '../../utils/contact-column-value';
import { formatContactSourceLabel } from '../../utils/contact-source.utils';
import {
  getEmailChannelStatus,
  getSmsChannelStatus,
} from '../../utils/contact-subscription-status';

const NAME_AVATAR_COLORS = ['#0066B2', '#005494', '#1F8A46', '#B4690E', '#5B4FCF', '#D80032'];

const getNameAvatarColor = (contact: Contact) => {
  const seed = contact.ItemId || contact.Email || getContactDisplayName(contact);
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return NAME_AVATAR_COLORS[hash % NAME_AVATAR_COLORS.length];
};

const formatCreatedDate = (value?: string | null): string => {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return format(date, 'MMM d, yyyy');
  }
};

export type ContactsTableCellContext = {
  isListFilterActive: boolean;
  isRemovingFromList: boolean;
  onOpenEditContact: (contact: Contact) => void;
  onOpenRemoveFromList: (contact: Contact) => void;
  onOpenDeleteContact: (contact: Contact) => void;
  onCopyContactId: (contact: Contact) => void;
  listName?: string;
};

const renderTextCell = (value: string | undefined, muted = false) => (
  <span className={muted ? 'text-neutral-500' : 'text-neutral-700'}>{value || '—'}</span>
);

export const renderContactsTableCell = (
  columnId: ContactsTableColumnId,
  contact: Contact,
  customFieldDefinitions: CustomFieldDefinition[] = []
): ReactNode => {
  const customAttributeKey = parseCustomAttributeColumnKey(columnId);
  if (customAttributeKey) {
    const definition = customFieldDefinitions.find((field) => field.Key === customAttributeKey);
    if (!definition) return '—';
    return renderTextCell(getContactCustomFieldDisplayValue(contact, definition));
  }

  switch (columnId) {
    case 'name': {
      const displayName = getContactDisplayName(contact);
      return (
        <div className="flex max-w-full items-center gap-2.5 truncate">
          <div
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
            style={{ backgroundColor: getNameAvatarColor(contact) }}
          >
            {getContactInitials(contact)}
          </div>
          <span className="truncate font-medium text-neutral-900">{displayName}</span>
        </div>
      );
    }
    case 'email': {
      const status = getEmailChannelStatus(contact);
      return (
        <div className="flex flex-col items-start gap-1">
          {renderTextCell(contact.Email, true)}
          {status ? <ContactSubscriptionStatusPill status={status} /> : null}
        </div>
      );
    }
    case 'phone': {
      const phone = contact.Phone?.trim();
      if (!phone) return <span className="text-neutral-400">—</span>;
      const status = getSmsChannelStatus(contact);
      return (
        <div className="flex flex-col items-start gap-1">
          {renderTextCell(phone, true)}
          {status ? <ContactSubscriptionStatusPill status={status} /> : null}
        </div>
      );
    }
    case 'source': {
      const source = formatContactSourceLabel(contact);
      if (!source) return '—';
      return (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 whitespace-nowrap">
          <DatabaseIcon className="size-[13px] shrink-0 text-neutral-500" strokeWidth={1.75} />
          {source}
        </span>
      );
    }
    case 'lists':
      return renderTextCell(getContactLists(contact).join(', ') || undefined, true);
    case 'created':
      return renderTextCell(formatCreatedDate(contact.CreatedDate), true);
    default:
      return renderTextCell(getContactColumnDisplayValue(columnId, contact));
  }
};

export const ContactsTableActionsCell = ({
  contact,
  context,
}: {
  contact: Contact;
  context: ContactsTableCellContext;
}) => {
  const displayName = getContactDisplayName(contact);

  return (
    <TableCell
      className="w-12 px-4 py-3 text-center align-middle"
      onClick={(event) => event.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-8 text-neutral-500 hover:text-neutral-800"
            aria-label={`Actions for ${displayName}`}
          >
            <DotsThreeVertical className="size-[18px]" weight="bold" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-[10rem] rounded-lg border-neutral-200 p-1.5 shadow-e3"
        >
          <DropdownMenuItem className="gap-2.5" onSelect={() => context.onOpenEditContact(contact)}>
            <PencilSimple className="size-4" aria-hidden />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem className="gap-2.5" onSelect={() => context.onCopyContactId(contact)}>
            <Copy className="size-4" aria-hidden />
            Copy ID
          </DropdownMenuItem>
          {context.isListFilterActive ? (
            <DropdownMenuItem
              className="gap-2.5"
              disabled={context.isRemovingFromList}
              onSelect={() => context.onOpenRemoveFromList(contact)}
            >
              <FolderMinus className="size-4" aria-hidden />
              Remove from {context.listName}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            className="gap-2.5 text-error focus:bg-error-background focus:text-error"
            onSelect={() => context.onOpenDeleteContact(contact)}
          >
            <Trash className="size-4" aria-hidden />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  );
};
